import React, { useCallback, useMemo, useState } from 'react';
import { useUser } from 'shared/hooks/use-user';
import {
  getUpcomingCalendarViewOptions,
  setUpcomingCalendarViewOptions,
} from 'shared/services/local-storage';
import { ID } from 'shared/types/id';
import { UpcomingCalendarViewOptions } from 'shared/types/upcoming-calendar-view-options';

import { UpcomingCalendarViewOptionsContext } from './context';

export type UpcomingCalendarViewOptionsProviderProps = {
  children: React.ReactNode;
};

export const UpcomingCalendarViewOptionsProvider: React.FC<
  UpcomingCalendarViewOptionsProviderProps
> = ({ children }) => {
  const user = useUser();
  const uid = user?.uid;

  const [state, setState] = useState<{
    uid?: ID;
    viewOptions: UpcomingCalendarViewOptions;
  }>(() => ({ uid, viewOptions: getUpcomingCalendarViewOptions(uid) }));

  // The uid arrives after the first render, so re-seed rather than leak the signed-out entry.
  if (state.uid !== uid) {
    setState({ uid, viewOptions: getUpcomingCalendarViewOptions(uid) });
  }

  const updateViewOptions = useCallback(
    (update: Partial<UpcomingCalendarViewOptions>) =>
      setState((previous) => {
        const viewOptions = { ...previous.viewOptions, ...update };
        setUpcomingCalendarViewOptions(previous.uid, viewOptions);

        return { ...previous, viewOptions };
      }),
    [],
  );

  const value = useMemo(
    () => ({ viewOptions: state.viewOptions, updateViewOptions }),
    [state.viewOptions, updateViewOptions],
  );

  return (
    <UpcomingCalendarViewOptionsContext.Provider value={value}>
      {children}
    </UpcomingCalendarViewOptionsContext.Provider>
  );
};
